import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, ChevronDown, ListChecks, Lock, LogOut, Menu, ShieldCheck, Store, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/components/account/nav";
import CartDrawer from "@/components/CartDrawer";
import ConsignBanner from "@/components/account/ConsignBanner";
import WizardModal from "@/components/account/WizardModal";
import ChatDock from "@/components/account/ChatDock";

const Ctx = createContext({ data: null, reload: () => { } });
export const useBackOffice = () => useContext(Ctx);

const isOn = (loc, to) => {
    if (!to) return false;
    const [path, query] = to.split("#")[0].split("?");
    if (loc.pathname !== path) return false;
    return query ? loc.search.includes(query) : !loc.search;
};

const slug = (s) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

function Item({ item, stats, loc, onGo, rooms, onWizard }) {
    const Icon = item.icon;
    const count = item.badge ? stats?.[item.badge] : null;
    const locked = !!(item.room && rooms?.[item.room]?.consigned);

    if (item.soon) {
        return (
            <span data-testid={`bo-soon-${slug(item.label)}`}
                className="flex cursor-default items-center gap-2 border-l-2 border-transparent px-2 py-[3px] text-sm text-[#4a4a4a]">
                <Icon size={14} className="shrink-0" />
                <span className="truncate">{item.label}</span>
                <span className="ml-auto text-[11px] font-semibold uppercase text-[#4a4a4a]">soon</span>
            </span>
        );
    }

    // The setup checklist is a window, not a page — it opens over whatever you were doing.
    if (item.to === "/account/wizard") {
        return (
            <button onClick={() => { onWizard(); onGo?.(); }} data-testid={`bo-nav-${slug(item.label)}`}
                className="flex w-full items-center gap-2 border-l-2 border-transparent px-2 py-[3px] text-left text-sm text-[#1a1a1a] hover:bg-white">
                <Icon size={14} className="shrink-0" />
                <span className="truncate">{item.label}</span>
            </button>
        );
    }

    const on = isOn(loc, item.to);
    return (
        <Link to={item.to} onClick={onGo} data-testid={`bo-nav-${slug(item.label)}`}
            className={`flex items-center gap-2 border-l-2 px-2 py-[3px] text-sm ${on
                ? `${locked ? "border-[#b42318]" : "border-[#1a1a1a]"} bg-white font-semibold text-[#1a1a1a]`
                : "border-transparent text-[#1a1a1a] hover:bg-white"}`}>
            <Icon size={14} className="shrink-0" />
            <span className="truncate">{item.label}</span>
            <span className="ml-auto flex shrink-0 items-center gap-1">
                {locked && (
                    <Lock size={11} className="text-[#b42318]" data-testid={`bo-nav-locked-${slug(item.label)}`}
                        title="On consignment — 13th Ave prints it, this shelf is locked" />
                )}
                {count ? (
                    <span data-testid={`bo-nav-count-${slug(item.label)}`}
                        className={item.alert
                            ? "min-w-[18px] rounded-full bg-[#d92d20] px-1.5 text-center text-[11px] font-bold leading-[18px] tabular-nums text-white"
                            : "text-[12px] font-semibold tabular-nums text-[#1a1a1a]"}>{count}</span>
                ) : null}
            </span>
        </Link>
    );
}

const MenuRow = ({ to, children, testid, onGo, external }) => (
    <Link to={to} onClick={onGo} data-testid={testid}
        className="flex items-center gap-2 border-t border-[#d0d0d0] px-2.5 py-1.5 text-[13px] font-medium text-[#1a1a1a] first:border-t-0 hover:bg-[#f5f5f5]">
        {children}
        {external && <ArrowUpRight size={12} className="ml-auto shrink-0" />}
    </Link>
);

/** The shop menu that replaces 13thav's own nav while you're behind the counter. */
function StoreMenu({ store, isAdmin, onSignOut }) {
    const [open, setOpen] = useState(false);
    const box = useRef(null);
    useEffect(() => {
        const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", away);
        return () => document.removeEventListener("mousedown", away);
    }, []);
    const close = () => setOpen(false);

    return (
        <div className="relative" ref={box}>
            <button onClick={() => setOpen((v) => !v)} data-testid="bo-store-switcher"
                className="inline-flex max-w-[16rem] items-center gap-1.5 border border-[#4a4a4a] px-1.5 py-1 text-left hover:border-white">
                <span className="h-2.5 w-2.5 shrink-0" style={{ background: store.theme?.primary || "#ffffff" }} />
                <span className="truncate text-[13px] font-bold text-white" data-testid="bo-store-name">{store.name}</span>
                <ChevronDown size={12} className="shrink-0 text-[#c8c8cc]" />
            </button>
            {open && (
                <div className="absolute left-0 top-full z-50 mt-1 w-60 border border-[#d0d0d0] bg-white"
                    data-testid="bo-store-menu">
                    <p className="border-b border-[#d0d0d0] bg-[#fafafa] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                        {store.tier_label}{store.city ? ` · ${store.city}` : ""}
                    </p>
                    <MenuRow to="/account/store" testid="bo-menu-profile" onGo={close}>Store profile</MenuRow>
                    <MenuRow to="/account/drop-cards" testid="bo-menu-dropcards" onGo={close}>Instagram drop cards</MenuRow>
                    <MenuRow to={`/stores/${store.slug}`} testid="bo-menu-view" onGo={close} external>View my store</MenuRow>
                    <MenuRow to="/directory" testid="bo-menu-mall" onGo={close} external>Directory</MenuRow>
                    {isAdmin && (
                        <MenuRow to="/admin" testid="bo-menu-admin" onGo={close}>
                            <ShieldCheck size={12} /> Platform admin
                        </MenuRow>
                    )}
                    <button onClick={onSignOut} data-testid="bo-menu-signout"
                        className="flex w-full items-center gap-2 border-t border-[#d0d0d0] px-2.5 py-1.5 text-left text-[13px] font-medium text-[#b42318] hover:bg-[#f5f5f5]">
                        <LogOut size={12} /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * The shell every back-office screen sits in (ADD-ON 25): its own flat chrome —
 * no mall header, no pills, no light grey — one sorted menu on the left plane,
 * work on the white plane. A work screen, not a landing page.
 */
export default function BackOffice({ children }) {
    const { user, logout, isAdmin } = useAuth();
    const loc = useLocation();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [wizard, setWizard] = useState(false);

    const reload = useCallback(() => api.get("/back-office/summary")
        .then(({ data: d }) => setData(d)).catch(() => setData({ store: null })), []);
    useEffect(() => { reload(); }, [reload]);
    useEffect(() => { setOpen(false); }, [loc.pathname, loc.search]);

    /** Paid for a space, or tapped the checklist: the window opens on arrival. */
    useEffect(() => {
        if (loc.pathname === "/account/wizard" || new URLSearchParams(loc.search).get("setup") === "1") {
            setWizard(true);
        }
    }, [loc.pathname, loc.search]);

    const closeWizard = () => {
        setWizard(false);
        reload();
        if (loc.pathname === "/account/wizard" || loc.search.includes("setup=1")) {
            navigate("/account", { replace: true });
        }
    };

    const store = data?.store || null;
    const stats = data?.stats || {};
    const loading = data === null;
    const groups = loading ? [] : NAV.filter((g) => (!g.store || store) && (!g.admin || isAdmin));
    const onAdmin = loc.pathname.startsWith("/admin");
    const signOut = () => { logout(); navigate("/"); };
    const setup = data?.wizard || null;
    const setupProgress = setup?.progress || { done: 0, total: 9 };

    const menu = (
        <nav data-testid="back-office-nav">
            {loading && (
                <p className="px-2 py-1 text-sm text-[#4a4a4a]" data-testid="bo-nav-loading">Loading your shop…</p>
            )}
            {groups.map((g) => (
                <div key={g.id} className="mb-2">
                    {g.label && (
                        <p className="mb-0.5 border-b border-[#d0d0d0] px-2 pb-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                            {g.label}
                        </p>
                    )}
                    <div>
                        {g.items.filter((i) => !(i.noStoreOnly && store) && !(i.store && !store) && !(i.admin && !isAdmin)).map((i) => (
                            <Item key={i.label} item={i} stats={stats} loc={loc} rooms={data?.consignment?.rooms}
                                onWizard={() => setWizard(true)} onGo={() => setOpen(false)} />
                        ))}
                    </div>
                </div>
            ))}
            <div className="border-t border-[#d0d0d0] pt-1.5">
                <button onClick={signOut} data-testid="logout-button"
                    className="flex w-full items-center gap-2 border-l-2 border-transparent px-2 py-[3px] text-sm text-[#1a1a1a] hover:bg-white">
                    <LogOut size={14} /> Sign out
                </button>
            </div>
        </nav>
    );

    return (
        <Ctx.Provider value={{ data, reload }}>
            <div className="bo-floor min-h-screen">
                <header className="dark-block sticky top-0 z-50" data-testid="back-office-bar">
                    <div className="mx-auto flex max-w-[110rem] flex-wrap items-center gap-x-2.5 gap-y-1 px-2.5 py-1.5 sm:px-3">
                        <Link to="/" data-testid="bo-brand"
                            className="text-[13px] font-bold tracking-[0.08em] text-white hover:underline">13THAV</Link>
                        <span className="border border-white bg-white px-1.5 py-px text-[11px] font-bold uppercase tracking-[0.06em] text-[#1a1a1a]">
                            Back office
                        </span>
                        {loading ? (
                            <span className="text-[11px] font-medium uppercase text-[#c8c8cc]"
                                data-testid="bo-bar-loading">Loading your shop…</span>
                        ) : store ? (
                            <>
                                <StoreMenu store={store} isAdmin={isAdmin} onSignOut={signOut} />
                                <span className="hidden text-[11px] font-medium uppercase text-[#c8c8cc] md:inline">
                                    {store.tier_label}{store.city ? ` · ${store.city}` : ""}
                                </span>
                                {data?.consignment?.enabled && (
                                    <span className="border border-white px-1.5 py-px text-[11px] font-semibold uppercase text-white"
                                        data-testid="bo-consignment-chip">
                                        consignment {Math.round((data.consignment.rate || 0.2) * 100)}%
                                    </span>
                                )}
                                {setup && !setup.finished && (
                                    <button onClick={() => setWizard(true)} data-testid="bo-wizard-btn"
                                        className="ml-auto inline-flex items-center gap-1.5 border border-[#1a56db] bg-[#1a56db] px-2 py-1 text-[12px] font-semibold text-white bo-inv hover:bg-[#1544b0]">
                                        <ListChecks size={12} /> Continue setup — {setupProgress.done}/{setupProgress.total}
                                    </button>
                                )}
                                {isAdmin && (
                                    <div data-testid="bo-mode-toggle" role="tablist" aria-label="Switch back office"
                                        className={`inline-flex border border-white/70 p-0.5 text-[12px] font-semibold ${setup && !setup.finished ? "" : "ml-auto"}`}>
                                        {[{ to: "/account", on: !onAdmin, label: "My shop", Icon: Store, id: "shop" },
                                          { to: "/admin", on: onAdmin, label: "Platform admin", Icon: ShieldCheck, id: "admin" }].map((m) => (
                                            <Link key={m.id} to={m.to} role="tab" aria-selected={m.on} data-testid={`bo-mode-${m.id}`}
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 transition-colors ${m.on ? "bg-white text-[#1a1a1a]" : "text-white/75 hover:bg-white/15 hover:text-white"}`}>
                                                <m.Icon size={12} /> {m.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                                <Link to={`/stores/${store.slug}`} data-testid="bo-view-store"
                                    className={`inline-flex items-center gap-1 text-[12px] font-semibold text-white hover:underline ${setup && !setup.finished || isAdmin ? "" : "ml-auto"}`}>
                                    View my store <ArrowUpRight size={12} />
                                </Link>
                                <button onClick={signOut} data-testid="bo-sign-out"
                                    className="hidden items-center gap-1 text-[12px] font-semibold text-white hover:underline sm:inline-flex">
                                    <LogOut size={12} /> Sign out
                                </button>
                            </>
                        ) : (
                            <>
                                <span className="text-sm font-bold text-white" data-testid="bo-account-name">
                                    {user?.name || user?.email}
                                </span>
                                <span className="text-[11px] font-medium uppercase text-[#c8c8cc]">shopper account</span>
                                <Link to="/account/sell" data-testid="bo-open-shop"
                                    className="ml-auto inline-flex items-center gap-1.5 border border-[#1a56db] bg-[#1a56db] px-2 py-1 text-[12px] font-semibold text-white bo-inv hover:bg-[#1544b0]">
                                    <Store size={12} /> Open a shop
                                </Link>
                                <button onClick={signOut} data-testid="bo-sign-out"
                                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-white hover:underline">
                                    <LogOut size={12} /> Sign out
                                </button>
                            </>
                        )}
                    </div>
                </header>

                <ConsignBanner data={data} reload={reload} />

                {store && setup && !setup.finished && (
                    <div className="border-b border-[#d0d0d0] bg-[#eff4ff]" data-testid="bo-setup-bar">
                        <div className="mx-auto flex max-w-[110rem] flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5">
                            <span className="text-[13px] font-semibold text-[#1a1a1a]">
                                Continue setup — {setupProgress.done}/{setupProgress.total}
                            </span>
                            <span className="h-1.5 w-32 shrink-0 bg-white" data-testid="bo-setup-progress">
                                <span className="block h-full bg-[#1a56db]"
                                    style={{ width: `${Math.round((setupProgress.done / setupProgress.total) * 100)}%` }} />
                            </span>
                            <span className="hidden text-[13px] text-[#4a4a4a] sm:inline">
                                Finish the questions, or open the whole store in one click.
                            </span>
                            <button onClick={() => setWizard(true)} data-testid="bo-setup-open"
                                className="ml-auto inline-flex items-center gap-1.5 border border-[#1a56db] bg-[#1a56db] px-2 py-1 text-[12px] font-semibold text-white bo-inv hover:bg-[#1544b0]">
                                <ListChecks size={12} /> Open the checklist
                            </button>
                        </div>
                    </div>
                )}

                <div className="mx-auto flex max-w-[110rem] gap-2.5 p-2.5 sm:p-3">
                    <aside className="hidden w-[13rem] shrink-0 border border-[#d0d0d0] bg-[#fafafa] lg:block">
                        <div className="sticky top-[3.25rem] max-h-[calc(100vh-4.5rem)] overflow-y-auto p-1.5">{menu}</div>
                    </aside>

                    <div className="min-w-0 flex-1">
                        <button onClick={() => setOpen((v) => !v)} data-testid="bo-menu-toggle"
                            className="mb-2 flex w-full items-center gap-2 border border-[#d0d0d0] bg-white px-2.5 py-1.5 text-sm font-semibold text-[#1a1a1a] lg:hidden">
                            {open ? <X size={16} /> : <Menu size={16} />} Back office menu
                            <ChevronDown size={16} className={`ml-auto ${open ? "rotate-180" : ""}`} />
                        </button>
                        {open && (
                            <div className="mb-2.5 border border-[#d0d0d0] bg-white p-1.5 lg:hidden">{menu}</div>
                        )}
                        {children}
                    </div>
                </div>

                <footer className="border-t border-[#d0d0d0] bg-white" data-testid="bo-footer">
                    <div className="mx-auto flex max-w-[110rem] flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-[12px] text-[#4a4a4a]">
                        <span className="font-semibold text-[#1a1a1a]">13thav · back office</span>
                        <Link to="/directory" className="hover:underline" data-testid="bo-footer-mall">Directory</Link>
                        <Link to="/directory" className="hover:underline" data-testid="bo-footer-directory">Store directory</Link>
                        <Link to="/account/inbox" className="hover:underline" data-testid="bo-footer-inbox">Messages</Link>
                        <span className="ml-auto">Support · (347) 573-3875</span>
                    </div>
                </footer>
                <CartDrawer />
                {wizard && store && <WizardModal onClose={closeWizard} onSaved={reload} />}
                {store && !isAdmin && <ChatDock />}
            </div>
        </Ctx.Provider>
    );
}
